import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Infinity as InfinityIcon, Check, ArrowRight, Baby, Backpack, GraduationCap, Briefcase, Rocket, Heart } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useAppMode } from "@/context/AppModeContext";

const STAGES = [
  { key: "child", label: "Child", desc: "Playful, guided, safe", icon: Baby },
  { key: "teen", label: "Teenager", desc: "School, friends, confidence", icon: Backpack },
  { key: "student", label: "College Student", desc: "Independence & study", icon: GraduationCap },
  { key: "adult", label: "Adult", desc: "Manage your whole life", icon: Briefcase },
  { key: "professional", label: "Professional", desc: "Career & business power", icon: Rocket },
  { key: "senior", label: "Senior", desc: "Simple, safe, voice-first", icon: Heart },
];

const GOALS = [
  "Manage my money better", "Grow my career", "Reduce stress", "Study smarter",
  "Stay safe online", "Get more organised", "Start a business", "Learn to cook",
  "Communicate confidently", "Understand AI", "Prepare for a big change", "Be more independent",
];

export default function Onboarding() {
  const { user, patchUser } = useAuth();
  const { setMode } = useAppMode();
  const [step, setStep] = useState(0);
  const [stage, setStage] = useState(user?.life_stage || "adult");
  const [goals, setGoals] = useState([]);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const toggleGoal = (g) => setGoals((gs) => (gs.includes(g) ? gs.filter((x) => x !== g) : [...gs, g]));

  const pickStage = (k) => { setStage(k); setMode(k); };

  const finish = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/profile", { life_stage: stage, mode: stage, goals, onboarded: true });
      patchUser(data);
      navigate("/");
    } catch (e) {
      toast.error("Could not save. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background px-5 py-10 sm:py-16">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-2.5 mb-8">
          <div className="h-10 w-10 rounded-xl bg-primary grid place-items-center text-primary-foreground"><InfinityIcon className="w-5 h-5" /></div>
          <span className="font-head font-extrabold text-lg">inizio</span>
        </div>

        {step === 0 && (
          <div className="animate-rise space-y-6">
            <div>
              <h1 className="font-head font-extrabold text-3xl sm:text-4xl">Who are we adapting for?</h1>
              <p className="text-muted-foreground mt-2">The whole experience — language, difficulty, design and voice — adapts to this. You can change it anytime.</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {STAGES.map((s) => (
                <button key={s.key} onClick={() => pickStage(s.key)} data-testid={`onboard-stage-${s.key}`}
                  className={`text-left rounded-lg border-2 p-5 transition-all hover:-translate-y-0.5 ${stage === s.key ? "border-primary bg-primary/10" : "border-border bg-card"}`}>
                  <div className="flex items-center justify-between">
                    <s.icon className="w-7 h-7 text-primary" />
                    {stage === s.key && <span className="h-6 w-6 rounded-full bg-primary text-primary-foreground grid place-items-center"><Check className="w-4 h-4" /></span>}
                  </div>
                  <div className="font-head font-bold text-lg mt-3">{s.label}</div>
                  <div className="text-sm text-muted-foreground">{s.desc}</div>
                </button>
              ))}
            </div>
            <button onClick={() => setStep(1)} data-testid="onboard-next" className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground font-bold px-6 py-3 hover:brightness-110 transition">
              Continue <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {step === 1 && (
          <div className="animate-rise space-y-6">
            <div>
              <h1 className="font-head font-extrabold text-3xl sm:text-4xl">What do you want to work on?</h1>
              <p className="text-muted-foreground mt-2">Pick a few. We'll shape your home, goals and recommendations. (Optional)</p>
            </div>
            <div className="flex flex-wrap gap-2.5">
              {GOALS.map((g) => (
                <button key={g} onClick={() => toggleGoal(g)} data-testid={`onboard-goal-${g.slice(0,8)}`}
                  className={`rounded-full border-2 px-4 py-2.5 text-sm font-medium transition-colors ${goals.includes(g) ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card hover:border-primary"}`}>
                  {g}
                </button>
              ))}
            </div>
            <div className="flex gap-3">
              <button onClick={() => setStep(0)} className="rounded-full border border-border px-6 py-3 font-semibold hover:bg-secondary">Back</button>
              <button onClick={finish} disabled={busy} data-testid="onboard-finish" className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground font-bold px-6 py-3 hover:brightness-110 transition disabled:opacity-50">
                {busy ? "Setting up…" : "Enter inizio"} <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
